<template>
  <section
    ref="rootEl"
    data-accordion
    :data-open="open || undefined"
    class="rounded-[16px] bg-[white]/40"
  >
    <button
      type="button"
      :data-sticky="sticky || undefined"
      class="flex w-full items-center gap-16 p-16"
      :aria-expanded="open"
      :aria-controls="bodyId"
      @click="toggle()"
    >
      <span class="flex flex-1 items-center gap-12">
        <slot name="header" />
      </span>
      <RevIllustration
        data-chevron
        src="/mine-my-phone/icons/right--flat.svg"
        alt=""
        aria-hidden="true"
        class="rounded-full shrink-0 bg-[white] p-4"
        :width="18"
        :height="18"
      />
    </button>
    <div :id="bodyId" data-body>
      <!-- Carries no padding, so the row truly collapses to 0. -->
      <div data-clip>
        <div class="grid gap-16 px-16 pb-16 pt-8">
          <slot />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, useId, watch } from 'vue'

import { RevIllustration } from '@ds/components/Illustration'
import { useLenis } from 'lenis/vue'

// Generic disclosure. Local open state is fine: the URL-as-state rule governs
// route-driven modals, not in-page expand/collapse.
// `open` makes it controlled (single-open groups). `groupOpen` — true when the
// group already has an open member — tells a fresh open from a swap, where a
// sibling folds, possibly above this one, dragging this header off the top.
const props = defineProps<{
  sticky?: boolean
  open?: boolean
  groupOpen?: boolean
}>()
const emit = defineEmits<{ toggle: [open: boolean] }>()

// Lenis owns scrollTop and rewrites it every frame, so scrollIntoView is reverted.
const lenis = useLenis()

// Covers the `--raw-out-quart` fold (400ms) plus a frame.
const FOLD_MS = 500

const bodyId = useId()
const rootEl = ref<HTMLElement | null>(null)
let frame = 0
// eslint-disable-next-line vue/no-dupe-keys
const open = ref(false)

// Vue casts an absent Boolean prop to `false`, so the render state stays
// internal and mirrors the prop; standalone use simply never fires this.
watch(
  () => props.open,
  (value) => {
    if (value === undefined || value === open.value) return
    open.value = value
  },
)

function cancelFrame() {
  if (!frame) return
  cancelAnimationFrame(frame)
  frame = 0
}

// Park this header at the scrollport's top edge, so shrinking content above the
// reader can't strand them. `pastHeaderOnly` skips the cases where the header is
// still on screen and nothing needs correcting; a swap opts out, since its
// header is visible by definition and about to be dragged anyway.
function anchorHeader(pastHeaderOnly = true) {
  const el = rootEl.value
  if (!el) return

  const instance = lenis.value
  if (!instance) {
    el.scrollIntoView({ block: 'start' })
    return
  }

  // The section's unstuck top relative to that edge; negative once scrolled past.
  const offset = () =>
    el.getBoundingClientRect().top -
    instance.rootElement.getBoundingClientRect().top

  if (pastHeaderOnly && offset() >= 0) return

  // Re-aimed every frame because a swap's target keeps moving while the
  // outgoing sibling folds — which is what lets that fold stay animated.
  cancelFrame()
  const until = performance.now() + FOLD_MS
  let last = Number.NaN
  let still = 0
  const step = () => {
    const next = instance.actualScroll + offset()
    instance.scrollTo(next, { immediate: true })
    // Release as soon as the target settles; a shrink in place takes a frame or two.
    still = Math.abs(next - last) < 0.5 ? still + 1 : 0
    last = next
    if (still < 3 && performance.now() < until) {
      frame = requestAnimationFrame(step)
      return
    }
    frame = 0
    instance.resize() // Lenis's limit trails a content change by a frame.
  }
  frame = requestAnimationFrame(step)
}

onBeforeUnmount(cancelFrame)

function toggle() {
  // Captured before the emit: the owner updates `groupOpen` in response to it.
  const wasGroupOpen = props.groupOpen
  open.value = !open.value
  emit('toggle', open.value)
  if (!open.value) anchorHeader()
  else if (wasGroupOpen) anchorHeader(false)
}

defineExpose({ anchorHeader })
</script>

<style scoped>
/* Keep the clip in CSS: as `overflow-clip` eslint autofixes it to `text-clip`. And
   `clip`, never `hidden`, which would make this the sticky header's scrollport. */
[data-accordion] {
  overflow: clip;
  transition: background-color var(--raw-out-quart);

  &[data-open] {
    background-color: white;
  }
}

/* Animate height via grid-template-rows 0fr -> 1fr. */
[data-body] {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows var(--raw-out-quart);

  [data-accordion][data-open] & {
    grid-template-rows: 1fr;
  }
}

[data-clip] {
  min-height: 0;
  overflow: hidden;
  /* Stays visible through the collapse, then drops out of the tab order. */
  transition: visibility var(--raw-out-quart);

  [data-accordion]:not([data-open]) & {
    visibility: hidden;
  }
}

@media (prefers-reduced-motion: reduce) {
  [data-body],
  [data-clip] {
    transition: none;
  }
}

[data-chevron] {
  transform: rotate(90deg) rotateX(0deg);
  transition:
    transform var(--raw-out-quart),
    background-color var(--raw-out-quart);

  [data-accordion][data-open] & {
    transform: rotate(90deg) rotateY(180deg);
    background-color: color-mix(
      in oklab,
      var(--raw-lightgrey) 35%,
      transparent
    );
  }
}

[data-sticky] {
  position: sticky;
  top: 0;
  z-index: 1;
  transition: background-color var(--raw-out-quart);

  [data-accordion][data-open] & {
    background-color: white;

    &::after {
      content: '';
      position: absolute;
      inset-inline: 0;
      top: 100%;
      height: 16px;
      background: linear-gradient(to bottom, white, transparent);
      pointer-events: none;
    }
  }
}
</style>
